// Pure helpers for the "rate your products" sheet and the widget's rate prompt: turning the buyer's picks
// into the submit payload, and the points the demo mode fakes in place of the
// backend. Shared by the app and the buy-again widget, so kept framework-free.

export type RatingValue = 1 | 2 | 3 | 4 | 5;

// Points the backend awards (see AwardReviewPointsAction / PointReasonEnum):
// every stored rating earns the Rating reward, and one that carries a written
// note earns the ReviewText reward on top. Mirrored here only for demo mode —
// the real thank-you screen shows whatever the server returns.
export const POINTS_PER_RATING = 10;
export const POINTS_PER_NOTE = 40;

// Matches the backend's `responses.*.comment` max length.
export const NOTE_MAX_LENGTH = 2000;

export interface RatingResponse {
  productId: number;
  rating: RatingValue;
  comment?: string;
}

/**
 * One response per rated product. A note only travels with a rating: it is
 * trimmed, clipped to the backend's limit, and omitted entirely when blank so
 * a star-only submission looks exactly as it did before notes existed.
 */
export function buildRatingResponses(
  ratings: Record<number, RatingValue>,
  notes: Record<number, string>,
): RatingResponse[] {
  return (Object.entries(ratings) as [string, RatingValue][]).map(
    ([id, rating]) => {
      const productId = Number(id);
      const comment = (notes[productId] ?? "").trim().slice(0, NOTE_MAX_LENGTH);
      return comment ? { productId, rating, comment } : { productId, rating };
    },
  );
}

export function demoPointsFor(responses: RatingResponse[]): number {
  return responses.reduce(
    (sum, r) => sum + POINTS_PER_RATING + (r.comment ? POINTS_PER_NOTE : 0),
    0,
  );
}

/**
 * "review" or "reviews" for the thank-you's copy: plural only when more than
 * one product went out with a written note in this post.
 */
export function reviewsWord(responses: RatingResponse[]): "review" | "reviews" {
  return responses.filter((r) => r.comment).length > 1 ? "reviews" : "review";
}

// At most this many products fan out in the multi-gift hero.
export const MAX_GIFT_TILES = 3;

/**
 * The gift part of the post-rating thank-you, the same on the app and the
 * widget:
 * - none: no gift to offer;
 * - one: one gift, "{Product} is on us";
 * - many: two or more gifts — `tiles` are the products to fan out (the same
 *   one repeated when only one product is giftable), `headline` the title.
 * `offers` are the giftable products the post rated, in display order;
 * `giftsAvailable` is how many gifts the points cover now.
 */
export type ThankYouGifts<T extends { title: string }> =
  | { kind: "none" }
  | { kind: "one"; gift: T }
  | { kind: "many"; count: number; tiles: T[]; headline: string };

export function thankYouGifts<T extends { title: string }>(
  offers: T[],
  giftsAvailable: number,
): ThankYouGifts<T> {
  const available = Math.max(0, Math.floor(giftsAvailable));
  if (offers.length === 0 || available === 0) return { kind: "none" };
  if (available < 2) return { kind: "one", gift: offers[0] };
  if (offers.length >= 2) {
    return {
      kind: "many",
      count: Math.min(available, offers.length),
      tiles: offers.slice(0, MAX_GIFT_TILES),
      headline: `${Math.min(available, offers.length)} free gifts are waiting`,
    };
  }
  const gift = offers[0];
  return {
    kind: "many",
    count: available,
    tiles: Array.from({ length: Math.min(available, MAX_GIFT_TILES) }, () => gift),
    headline: `${available} free ${pluralTitle(gift.title)} are waiting`,
  };
}

/** "Rose Nectar" → "Rose Nectars"; "Rose Kiss" → "Rose Kisses". */
export function pluralTitle(title: string): string {
  const t = title.trim();
  if (t === "") return "gifts";
  return /(s|x|z|ch|sh)$/i.test(t) ? `${t}es` : `${t}s`;
}

/** The line under the thank-you's headline. */
export function thankYouLine(
  kind: ThankYouGifts<{ title: string }>["kind"],
  word: "review" | "reviews",
): string {
  if (kind === "many") return `Thanks for your ${word}. Pick yours, or send them to friends.`;
  if (kind === "one") return `Thanks for your ${word}. Keep one for yourself, or send two to friends.`;
  return "You're helping others discover great products.";
}
